import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import Datagrid from "@ui/components/DataGrid";
import SearchBar from "@ui/components/SearchBar";
import { getSelectedId, handleSearch } from "../../../../ui/helpers";
import { HEADINGS, searchMap } from "../../Organisations/constants";
import {
  removeLaboratory,
  setLaboratoriesList,
  unarchiveLaboratory,
} from "./_redux/actions";
import { renderLaboratoriesRow } from "./helpers";
import { ListButton } from "../../../../ui/components/ListButton";
import { ListHeader } from "../../../../ui/structures/ListHeader";
import { useFetch } from "../../../../hooks/fetch.hook";
import {
  archiveOrganisation,
  getOrganisationById,
  modifyOrganisation,
} from "../../Organisations/_api";
import {
  modifyOrganisationData,
  modifyOrganisations,
} from "../../Organisations/helpers";
import { UnarchiveButton } from "../../../../ui/components/UnarchiveButton";
import { Modal } from "../../../../ui/components/Modal";
import { ConfirmDelete } from "../../../../ui/components/ConfirmDelete";
import { ArchiveCheckbox } from "../../../../ui/components/ArchiveCheckbox";
import { sortHeaderStyle } from "../../../constants";
import { TableSortLabel } from "@material-ui/core";
import { getLaboratories } from "./_api";
import { info } from "../../../../helpers/toasts";
import { Loader } from "../../../../ui/components/Loader";

export const List = () => {
  const dispatch = useDispatch();
  const { request } = useFetch();

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [focused, setFocused] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [unArchiving, setUnarchiving] = useState(false);
  const [field, setField] = useState("");
  const [direction, setDirection] = useState("");

  const handleModalClose = () => setConfirmDeleteOpen(false);

  const data = useSelector(
    ({ laboratories: { laboratoriesList } }) => laboratoriesList
  );

  const fetchLaboratories = () => {
    setLoading(true);
    request(getLaboratories, search, archived, field, field && direction)
      .then((data) => data && modifyOrganisations(data))
      .then((data) => {
        if (!data) return;
        dispatch(setLaboratoriesList(data));
        setInitialData(data);
      })
      .finally(() => {
        setArchivedLoading(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchLaboratories();
    // eslint-disable-next-line
  }, [search, archived, field, direction]);

  const KEY_MAP = {
    "Business Name": data.map((item) => item.trading_name),
    "Phone Number": data.map((item) => item.phone),
    "Email Address": data.map((item) => item.email),
    "ABN Status": data.map((item) => item.abn_verified),
  };

  const unArchiveDisabled = useMemo(
    () => Object.values(selected).filter(Boolean).length !== 1,
    [selected]
  );

  const handleArchive = (id, setSubmitting) => {
    setConfirmDeleteOpen(false);
    setActionLoading(true);
    setSubmitting(true);
    request(archiveOrganisation, id)
      .then((data) => {
        if (!data) return;
        if (archived) {
          request(getOrganisationById, id).then(
            (data) =>
              data &&
              dispatch(unarchiveLaboratory(modifyOrganisationData(data)))
          );
        } else dispatch(removeLaboratory(id));
        info("Laboratory has been archived!");
        setSelected({});
      })
      .finally(() => {
        setSubmitting(false);
        setActionLoading(false);
      });
  };

  const handleUnarchive = () => {
    const laboratoryID = getSelectedId(selected);
    const status = (data.find(({ id }) => id === laboratoryID) || {}).status;
    if (status !== "archived") return;
    setActionLoading(true);
    setUnarchiving(true);
    request(modifyOrganisation, { status: "active" }, laboratoryID)
      .then((data) => {
        if (!data) return;
        dispatch(unarchiveLaboratory(modifyOrganisationData(data)));
        info("Laboratory has been unarchived!");
        setSelected({});
      })
      .finally(() => {
        setUnarchiving(false);
        setActionLoading(false);
      });
  };

  const renderButtons = () => (
    <>
      {archived && (
        <UnarchiveButton
          handleClick={handleUnarchive}
          disabled={unArchiveDisabled}
          loading={unArchiving}
        />
      )}
      <div className="mr-5">
        <Link
          to={{
            pathname: `/add-laboratory`,
          }}
          data-testid="new"
        >
          <ListButton label="New" />
        </Link>
      </div>
    </>
  );

  const showArchived = () => {
    setArchivedLoading(true);
    setArchived(!archived);
  };

  const handleOpenModal = (id) => {
    setConfirmDeleteOpen(true);
    setSelectedItem(id);
  };

  const resetSearch = () => {
    dispatch(setLaboratoriesList(initialData));
    setSearch("");
  };

  const handleFocus = () => setFocused(true);

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={sortHeaderStyle}
        className="px-5 text-nowrap"
        onClick={() => {
          setDirection(
            key !== field ? "desc" : direction === "desc" ? "asc" : "desc"
          );
          setField(key);
        }}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {key === field && <TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  return (
    <>
      {actionLoading && <Loader title="Loading..." isOpen={actionLoading} />}
      {confirmDeleteOpen && (
        <Modal
          isOpen={confirmDeleteOpen}
          submitable
          onClose={handleModalClose}
          maxWidth="sm"
          modalContent={
            <ConfirmDelete
              handleClose={handleModalClose}
              handleSubmit={handleArchive}
              id={selectedItem}
              name={
                (data.find(({ id }) => id === selectedItem) || {}).legal_name
              }
            />
          }
        />
      )}
      <div className="row justify-content-center mt-10">
        <div className="col-12">
          <div>
            <SearchBar
              onSearch={(data) => handleSearch(data, searchMap, setSearch)}
              clearSearch={resetSearch}
              keyMap={KEY_MAP}
              placeholder="Filter laboratories..."
              elevation={2}
              chipBackgroundColor="#E8F5E1"
              chipColor="#255915"
              focused={focused}
              setFocused={setFocused}
            />
          </div>
          <div className="bg-white rounded py-7 px-10">
            <ListHeader
              title="Laboratories"
              renderButtons={renderButtons}
              handleFocus={handleFocus}
            />
            <ArchiveCheckbox
              archivedLoading={archivedLoading}
              archived={archived}
              showArchived={showArchived}
            />
            <Datagrid
              data={data}
              headings={HEADINGS}
              renderRow={renderLaboratoriesRow}
              renderHeaderWithSorting={renderHeaderWithSorting}
              selected={selected}
              setSelected={setSelected}
              deletable
              handleDelete={handleOpenModal}
              editable
              loading={loading}
              selectable
              link="laboratories"
            />
          </div>
        </div>
      </div>
    </>
  );
};
